import { z } from 'zod'
import { lenientNullable, timestampToDateNullable } from '@upadelmatch/core'

// `place_requests/{id}` — the ONE inbox for "this club should be in the
// directory and isn't" (spec/usersmatch/01 § Who owns a club row).
//
// Two origins, one resolution. From the operator's side a player typing a club
// we do not have and an owner asking to be listed are the same job: create a
// row, or merge into one that already exists under another name. Modelling
// them as two flows would mean two inboxes and two chances to ignore one.
//
// WHY THE PLAYER SIDE HAS NO BUTTON. The signal is already produced: the place
// picker offers "Escribir otra dirección" when nothing matches, and that typed
// text WAS being thrown away. Recording it captures demand rather than
// opinions — a place three matches were played at is worth adding; a place
// somebody typed once may be a typo. That is what `matchCount` counts, and it
// is why this is not a "suggest a club" form nobody would fill in.

export const PLACE_REQUEST_ORIGINS = ['player', 'club_owner'] as const
export type PlaceRequestOrigin = (typeof PLACE_REQUEST_ORIGINS)[number]

/**
 * `pending` → `created` (a new directory row) | `merged` (into an existing
 * one) | `rejected`. Terminal states keep the doc: the same club gets typed
 * again, and a rejected request is how we remember we already decided.
 */
export const PLACE_REQUEST_STATUSES = ['pending', 'created', 'merged', 'rejected'] as const
export type PlaceRequestStatus = (typeof PLACE_REQUEST_STATUSES)[number]

export const PlaceRequestSchema = z.object({
  id: z.string(),
  origin: lenientNullable(z.enum(PLACE_REQUEST_ORIGINS), 'placeRequest.origin'),
  /** What the player typed, verbatim. The whole point — never cleaned up. */
  typed: z.string().default(''),
  /** Lowercased `typed`, so repeats of the same club collapse onto one doc. */
  typedKey: z.string().default(''),
  /**
   * The requester's city — NOT the club's. We do not know the club's city;
   * that is why it is not in the directory. Renamed from `city` on 2026-08-11
   * because the operator's verify form pre-filled the club's city from it, so
   * a player in Granada typing a club in Órgiva proposed "Granada" as fact.
   * A proxy presented as a fact is worse than a blank — a blank gets checked.
   * Read it as a hint (people mostly type clubs near themselves), never as an
   * answer.
   */
  requesterCity: z.string().nullable().default(null),
  /** Set only when the origin is a club owner asking to be listed. */
  clubManagerId: z.string().nullable().default(null),
  /**
   * How many times this place has been typed. The priority signal: it is
   * demand, measured, not a vote. Incremented on repeat rather than creating a
   * second document.
   */
  matchCount: z.number().int().min(1).default(1),
  /** The uid that most recently asked. Not a list — this is not a petition. */
  lastRequestedBy: z.string().nullable().default(null),
  status: lenientNullable(z.enum(PLACE_REQUEST_STATUSES), 'placeRequest.status'),
  /** The directory row this resolved into, for `created` and `merged`. */
  resolvedPlaceId: z.string().nullable().default(null),
  createdAt: timestampToDateNullable,
  updatedAt: timestampToDateNullable,
})

export type PlaceRequestDoc = z.output<typeof PlaceRequestSchema>

/**
 * The document id, derived from the typed text so the SAME place typed twice
 * lands on the same doc and increments instead of filling the inbox with
 * duplicates of one club.
 *
 * Deliberately crude: lowercase, strip accents, collapse everything that is
 * not a letter or digit. "Padel Sur Granada", "padel sur granada" and
 * "Pádel-Sur  Granada" are one request. It will not catch "Padel Sur" vs
 * "Club Padel Sur", and it is not supposed to — merging those is the
 * operator's judgement, which is what the merge action is for.
 */
export function placeRequestKey(typed: string): string {
  return typed
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
}
